import { useState } from 'react';
import styled from 'styled-components';
import { FrostedGlassLight } from '@seed-health/tokens';
import { mqLargeMin } from 'styled/tools/media-queries';
import { InView } from 'react-intersection-observer';
import { mqSmallMin } from 'styled/tools/media-queries';
import type { EssentialsProposition } from 'lib/pdp-modules/types';

const PropositionContainer = styled.div`
  margin-top: 3rem;
  margin-right: -1rem;
  padding-right: 1rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  overflow: auto;
  scroll-snap-type: x mandatory;
  z-index: 1;

  &::-webkit-scrollbar {
    display: none;
  }

  @media ${mqLargeMin} {
    margin-top: 7rem;
    margin-right: 0;
    border-radius: 1rem;
    padding: 2rem 3rem;
    gap: 3rem;
    background: var(--outline-light-20);
    backdrop-filter: ${FrostedGlassLight};
    -webkit-backdrop-filter: ${FrostedGlassLight};
    scroll-snap-type: none;
  }
`;

const PropositionDots = styled.div`
  margin: 1.5rem 0.5rem 0.5rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;

  @media ${mqLargeMin} {
    display: none;
  }
`;

const PropositionDot = styled.div`
  border-radius: 100px;
  width: 0.5rem;
  height: 0.5rem;
  background-color: var(--fill-primary-t20);
  backdrop-filter: blur(20px);

  &.selected {
    width: 1rem;
    background-color: white;
  }
`;

const Proposition = styled.div`
  border-radius: 1rem;
  padding: 1.5rem;
  flex-shrink: 0;
  max-width: 100%;
  width: 18.75rem;
  scroll-snap-align: start;
  background: var(--color-primary-snow-white-10);
  backdrop-filter: ${FrostedGlassLight};
  -webkit-backdrop-filter: ${FrostedGlassLight};

  @media ${mqSmallMin} {
    min-width: 18.75rem;
    flex-shrink: 1;
    scroll-snap-align: none;
  }

  @media ${mqLargeMin} {
    border: 0;
    padding: 0;
    min-width: 0;
    width: auto;
    flex: 1;
    background: transparent;
    backdrop-filter: unset;
    -webkit-backdrop-filter: unset;
  }
`;

const PropositionImage = styled.img`
  margin: 1.25rem 0;
  height: 2.5rem;

  @media ${mqLargeMin} {
    height: 4rem;
  }
`;

const PropositionTitle = styled.p`
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  font-weight: 500;
  letter-spacing: -0.2px;
`;

const PropositionContent = styled.p`
  font-weight: 350;
  line-height: 140%;
  letter-spacing: -0.16px;
`;

const PropositionCards = ({
  propositions,
}: {
  propositions: EssentialsProposition[];
}) => {
  const [firstVisibleProp, setFirstVisibleProp] = useState(0);
  const checkTiles = (_inView, _entry) => {
    try {
      // ensure callback is after classNames updated
      setTimeout(() => {
        const firstVisibleProposition = document.querySelector(
          '.proposition-visible',
        );
        if (firstVisibleProposition) {
          const index = firstVisibleProposition.getAttribute(
            'data-proposition-index',
          );
          setFirstVisibleProp(parseInt(index));
        }
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <PropositionContainer>
        {propositions.map((proposition, titleIndex) => (
          <InView
            key={`essentials-proposition-${titleIndex}`}
            onChange={checkTiles}
            threshold={0.75}
          >
            {({ inView, ref }) => (
              <Proposition
                ref={ref}
                className={`${inView ? 'proposition-visible' : ''}`}
                data-proposition-index={titleIndex}
              >
                {proposition.icon && (
                  <PropositionImage
                    src={proposition.icon.url}
                    alt={proposition.icon.alt}
                  />
                )}
                <PropositionTitle>{proposition.title}</PropositionTitle>
                <PropositionContent>{proposition.text}</PropositionContent>
              </Proposition>
            )}
          </InView>
        ))}
      </PropositionContainer>
      <PropositionDots>
        {propositions.map((_proposition, titleIndex) => (
          <PropositionDot
            key={`proposition-dot-${titleIndex}`}
            className={firstVisibleProp === titleIndex ? 'selected' : ''}
          ></PropositionDot>
        ))}
      </PropositionDots>
    </>
  );
};

export default PropositionCards;
